// Apple-style app launcher overlay.
//
// Source-of-truth: portal-standalone.html L4730-4800 (#ios-launcher /
// openAppLauncher / launchApp). The source uses an iPhone-homescreen-
// style swipeable paginated grid. We render a single scrollable grid
// instead — same tile aesthetic, simpler interaction, sufficient at
// desktop widths and easier to keyboard-navigate.
//
// Tiles route to platform pages or trigger shell actions (open the
// new-customer modal, etc.). Search filters the grid in real time.

import { renderIcon, lockScroll, unlockScroll, type IconName } from "@tkos/shared-ui";

import { openNewCustomerModal } from "./new-customer-modal";
import { navigate } from "./router";
import { openSpotlight } from "./spotlight";
import type { ToastKind } from "./types";

import "./app-launcher.css";

interface LauncherDeps {
  showToast: (msg: string, kind?: ToastKind) => void;
}

interface AppTile {
  id: string;
  label: string;
  icon: IconName;
  bg: string;
  iconColor: string;
  run: (deps: LauncherDeps) => void;
}

const OVERLAY_ID = "tkos-applauncher";

let overlay: HTMLElement | null = null;
let inputEl: HTMLInputElement | null = null;
let gridEl: HTMLElement | null = null;
let escListener: ((e: KeyboardEvent) => void) | null = null;
let currentTiles: AppTile[] = [];

function appsCatalog(): AppTile[] {
  return [
    { id: "dashboard",    label: "Dashboard",    icon: "dashboard", bg: "rgba(229,130,0,.12)", iconColor: "#e58200", run: () => navigate("dashboard") },
    { id: "submissions",  label: "Submissions",  icon: "file",      bg: "rgba(59,130,246,.12)", iconColor: "#3b82f6", run: () => navigate("submissions") },
    { id: "messages",     label: "Messages",     icon: "chat",      bg: "rgba(34,197,94,.12)",  iconColor: "#22c55e", run: () => navigate("messages") },
    { id: "calendar",     label: "Calendar",     icon: "calendar",  bg: "rgba(168,85,247,.12)", iconColor: "#a855f7", run: () => navigate("calendar") },
    { id: "bookings",     label: "Bookings",     icon: "calendar",  bg: "rgba(20,184,166,.12)", iconColor: "#14b8a6", run: () => navigate("bookings") },
    { id: "deliveries",   label: "Deliveries",   icon: "truck",     bg: "rgba(249,115,22,.12)", iconColor: "#f97316", run: () => navigate("deliveries") },
    { id: "suppliers",    label: "Suppliers",    icon: "package",   bg: "rgba(14,165,233,.12)", iconColor: "#0ea5e9", run: () => navigate("suppliers") },
    { id: "crm",          label: "CRM",          icon: "team",      bg: "rgba(236,72,153,.12)", iconColor: "#ec4899", run: () => navigate("crm") },
    { id: "contacts",     label: "Contacts",     icon: "team",      bg: "rgba(6,182,212,.12)",  iconColor: "#06b6d4", run: () => navigate("contacts") },
    { id: "operations",   label: "Operations",   icon: "gear",      bg: "rgba(139,92,246,.12)", iconColor: "#8b5cf6", run: () => navigate("operations") },
    { id: "drivers",      label: "Drivers",      icon: "truck",     bg: "rgba(16,185,129,.12)", iconColor: "#10b981", run: () => navigate("drivers") },
    { id: "reports",      label: "Reports",      icon: "chart",     bg: "rgba(20,184,166,.12)", iconColor: "#14b8a6", run: () => navigate("reports") },
    { id: "quote",        label: "Quote Builder",icon: "pound",     bg: "rgba(34,197,94,.12)",  iconColor: "#22c55e", run: () => navigate("quote-builder") },
    { id: "designs",      label: "Designs",      icon: "file",      bg: "rgba(99,102,241,.12)", iconColor: "#6366f1", run: () => navigate("designs") },
    { id: "products",     label: "Products",     icon: "search",    bg: "rgba(99,102,241,.12)", iconColor: "#6366f1", run: () => navigate("product-search") },
    { id: "catalogue",    label: "Catalogue",    icon: "folder",    bg: "rgba(245,158,11,.12)", iconColor: "#f59e0b", run: () => navigate("catalogue-manager") },
    // Social AI + AI Renders removed from nav for V1 (return in V1.5 when
    // properly built); route + modal code kept dormant for easy restore.
    { id: "new-customer", label: "New Customer", icon: "customer",  bg: "rgba(245,158,11,.12)", iconColor: "#f59e0b", run: (deps) => { navigate("crm"); openNewCustomerModal({ showToast: deps.showToast }); } },
    { id: "search",       label: "Search",       icon: "search",    bg: "rgba(113,113,122,.12)",iconColor: "#71717a", run: (deps) => openSpotlight(deps) },
    { id: "settings",     label: "Settings",     icon: "gear",      bg: "rgba(113,113,122,.12)",iconColor: "#71717a", run: () => navigate("settings") },
  ];
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function paint(filter: string, deps: LauncherDeps): void {
  if (!gridEl) return;
  const q = filter.trim().toLowerCase();
  const all = appsCatalog();
  currentTiles = q ? all.filter((a) => a.label.toLowerCase().includes(q)) : all;
  if (currentTiles.length === 0) {
    gridEl.innerHTML = `<div class="tkos-launch__empty">No apps match.</div>`;
    return;
  }
  gridEl.innerHTML = currentTiles
    .map(
      (a, i) => `
      <button type="button" class="tkos-launch__tile" data-i="${i}" aria-label="${escapeHtml(a.label)}">
        <span class="tkos-launch__icon" style="background:${escapeHtml(a.bg)};color:${escapeHtml(a.iconColor)}">
          ${renderIcon(a.icon, { size: "md" }).outerHTML}
        </span>
        <span class="tkos-launch__label">${escapeHtml(a.label)}</span>
      </button>`,
    )
    .join("");
  for (const btn of gridEl.querySelectorAll<HTMLButtonElement>("button[data-i]")) {
    btn.addEventListener("click", () => {
      const idx = Number(btn.dataset["i"]);
      const tile = currentTiles[idx];
      if (!tile) return;
      close();
      try {
        tile.run(deps);
      } catch (err) {
        console.error("[app-launcher] tile run failed", err);
      }
    });
  }
}

function buildOverlay(deps: LauncherDeps): void {
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.id = OVERLAY_ID;
  overlay.className = "tkos-launch";

  const panel = document.createElement("div");
  panel.className = "tkos-launch__panel";

  const head = document.createElement("div");
  head.className = "tkos-launch__head";

  const searchWrap = document.createElement("div");
  searchWrap.className = "tkos-launch__search";
  searchWrap.appendChild(renderIcon("search", { size: "sm" }));

  inputEl = document.createElement("input");
  inputEl.type = "search";
  inputEl.className = "tkos-launch__input";
  inputEl.placeholder = "Search apps…";
  inputEl.autocomplete = "off";
  inputEl.spellcheck = false;
  searchWrap.appendChild(inputEl);

  const close = document.createElement("button");
  close.type = "button";
  close.className = "tkos-launch__close";
  close.setAttribute("aria-label", "Close app launcher");
  close.innerHTML = "&times;";
  close.addEventListener("click", () => closeOverlay());

  head.append(searchWrap, close);

  gridEl = document.createElement("div");
  gridEl.className = "tkos-launch__grid";

  panel.append(head, gridEl);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);

  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeOverlay();
  });
  inputEl.addEventListener("input", () => paint(inputEl?.value ?? "", deps));
  inputEl.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeOverlay();
    }
    if (e.key === "Enter") {
      // Run the first visible tile
      const first = currentTiles[0];
      if (first) {
        e.preventDefault();
        closeOverlay();
        try {
          first.run(deps);
        } catch (err) {
          console.error("[app-launcher] tile run failed", err);
        }
      }
    }
  });
}

function open(deps: LauncherDeps): void {
  buildOverlay(deps);
  if (!overlay || !inputEl) return;
  overlay.classList.add("is-open");
  lockScroll();
  inputEl.value = "";
  paint("", deps);
  setTimeout(() => inputEl?.focus(), 30);
  if (!escListener) {
    escListener = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && overlay?.classList.contains("is-open")) {
        e.preventDefault();
        closeOverlay();
      }
    };
    document.addEventListener("keydown", escListener);
  }
}

function closeOverlay(): void {
  if (!overlay) return;
  overlay.classList.remove("is-open");
  unlockScroll();
  if (escListener) {
    document.removeEventListener("keydown", escListener);
    escListener = null;
  }
}

// Locally-scoped alias so click handlers inside this module can call it
function close(): void {
  closeOverlay();
}

export function openAppLauncher(deps: LauncherDeps): void {
  open(deps);
}
